import styled from 'styled-components';
import { useState } from 'react';
import Input from '../../Input/Input';
import { useTranslation } from 'react-i18next';
import Icon from '../../Icon/Icon';
import { black } from '../../../utils/style/colors';
import { v1 as uuid } from 'uuid';
import Button from '../../Button/Button';
import { appDispatch, RootState } from '../../../store/store';
import { updateProvider } from '../../../store/providers/providerSlice';
import { useSelector } from 'react-redux';
import notify from '../../Notify/Notify';
import { IProvider } from '../../../store/providers/providerTypes';
import Textarea from '../../Textarea';

const Form = styled.form`
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

interface IDefaultPrompts {
  key: string;
  value: string;
}

interface IProps {
  providerId: string;
}

const Vira: React.FC<IProps> = ({ providerId }) => {
  const providers = useSelector((state: RootState) => state.providers);
  const provider = providers?.map?.[providerId].data;
  const botProperties = provider?.botProperties;
  const [botIntegration, setBotIntegration] = useState<boolean>(
    botProperties?.enableBotIntegration ?? false
  );
  const [systemInstructions, setSystemInstructions] = useState<string>(
    botProperties?.systemInstructions ?? ''
  );
  const [chatButtonTitle, setChatButtonTitle] = useState<string>(
    botProperties?.chatButtonTitle ?? ''
  );
  const [model, setModel] = useState<string>(botProperties?.model ?? '');
  const [chatHistoryItemId, setChatHistoryItemId] = useState<string>(
    botProperties?.chatHistoryItemId ?? ''
  );
  const [chatHistoryItemViewToken, setChatHistoryItemViewToken] =
    useState<string>(botProperties?.chatHistoryItemViewToken ?? '');
  const [defaultPrompts, setDefaultPrompts] = useState<IDefaultPrompts[]>(
    () => {
      return (
        botProperties?.defaultPrompts.reduce(
          (acc: IDefaultPrompts[], prompt) => {
            acc.push({
              key: uuid(),
              value: prompt,
            });
            return acc;
          },
          []
        ) ?? []
      );
    }
  );
  const [showCitation, setShowCitation] = useState<boolean>(
    botProperties?.showCitation ?? false
  );
  const [saving, setSaving] = useState<boolean>(false);

  const { t } = useTranslation('documentManager');

  const submitHandler = (e: Event) => {
    e.preventDefault();
    setSaving(true);
    const botProperties = {
      ...provider?.botProperties,
      enableBotIntegration: botIntegration,
      defaultPrompts: defaultPrompts.reduce((acc: string[], item) => {
        acc.push(item.value);
        return acc;
      }, []),
      systemInstructions: systemInstructions,
      showCitation: showCitation,
      chatButtonTitle,
      chatHistoryItemId,
      model,
      chatHistoryItemViewToken,
    };
    appDispatch(
      updateProvider({
        provider: {
          ...provider,
          id: Number(providerId),
          botProperties: botProperties,
        } as IProvider,
      })
    )
      .then(() => {
        setSaving(false);
        notify(t('saveSuccess', { ns: 'documentManager' }));
      })
      .catch(() => {
        setSaving(false);
        notify(t('faultInSaving', { ns: 'documentManager' }), 'error');
      });
  };

  return (
    <Form onSubmit={submitHandler}>
      <div className="flex flex-row gap-10">
        <Input
          id={Math.random().toString()}
          label={t('botIntegration')}
          type="checkbox"
          checked={botIntegration}
          onChange={(e) => setBotIntegration(e.target.checked)}
        />
        <Input
          id={Math.random().toString()}
          label={t('showCitation')}
          type="checkbox"
          checked={showCitation}
          onChange={(e) => setShowCitation(e.target.checked)}
        />
      </div>
      <Input
        label={t('chatButtonTitle')}
        type={'text'}
        id={'chatButtonTitle'}
        value={chatButtonTitle}
        onChange={(e) => setChatButtonTitle(e.target.value)}
      />
      <Input
        label={t('model')}
        type={'text'}
        id={'model'}
        value={model}
        onChange={(e) => setModel(e.target.value)}
      />
      <Input
        label={t('chitHistoryItemId')}
        type={'text'}
        id={'chitHistoryItemId'}
        value={chatHistoryItemId}
        onChange={(e) => setChatHistoryItemId(e.target.value)}
      />
      <Input
        label={t('chatHistoryItemViewToken')}
        type={'text'}
        id={'chatHistoryItemViewToken'}
        value={chatHistoryItemViewToken}
        onChange={(e) => setChatHistoryItemViewToken(e.target.value)}
      />
      <Textarea
        id={Math.random().toString()}
        label={t('instructions')}
        value={systemInstructions}
        onChange={(e) => setSystemInstructions(e.target.value)}
      />
      <div className="flex gap-5 flex-col font-bold" style={{ color: black }}>
        <div className="flex items-center gap-3">
          <p>{t('defaultPrompts')}</p>
          <Icon
            id={`addPrompt-${Math.random().toString()}`}
            type="plus-circle"
            style={{ cursor: 'pointer' }}
            title={t('addDefaultPrompts')}
            onClick={() => {
              setDefaultPrompts((prevState) => [
                ...prevState,
                { key: uuid(), value: '' },
              ]);
            }}
          />
        </div>
        <div>
          {defaultPrompts?.map((item) => (
            <div className="flex items-center gap-3" key={item.key}>
              <Icon
                style={{
                  fontSize: '1.5rem',
                  display: 'flex',
                  textAlign: 'center',
                }}
                type="delete"
                onClick={() =>
                  setDefaultPrompts((prevState) =>
                    prevState.filter((prompt) => prompt.key !== item.key)
                  )
                }
              />
              <Input
                id={item.key}
                value={item.value}
                type="text"
                required={true}
                wrapperStyle={{ width: '90%' }}
                onChange={(e) => {
                  setDefaultPrompts((prevState) => {
                    return prevState.reduce(
                      (acc: IDefaultPrompts[], prompt) => {
                        if (prompt.key === item.key)
                          acc.push({ key: item.key, value: e.target.value });
                        else acc.push(prompt);
                        return acc;
                      },
                      []
                    );
                  });
                }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="items-end justify-end">
        <Button type="submit" color="black" loading={saving}>
          {t('saveBotSettings')}
        </Button>
      </div>
    </Form>
  );
};

export default Vira;
